import React, { useState } from 'react';

const MatchGrid = ({ matches = [], onSelectMatch, onDeleteMatch }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const getMatchWinner = (match) => {
    if (!match) return 'N/A';
    const statusStr = String(match.status || '').toLowerCase();
    const winnerStr = String(match.result || match.winner || '').toLowerCase();
    const isCompleted = statusStr === 'completed' || match.current_state?.matchCompleted === true || (Boolean(winnerStr) && winnerStr !== 'inprogress' && winnerStr !== 'in_progress');
    if (!isCompleted) {
      return 'N/A';
    }
    const isMe = (() => {
      const w = String(match.winner || match.result || '').trim().toLowerCase();
      if (w === 'me' || w === 'player1' || w === 'won') return true;
      if (w === 'opponent' || w === 'player2' || w === 'lost') return false;
      if (match.won === true) return true;
      if (match.won === false) return false;
      const mySets = parseInt(match.my_sets_won ?? match.mySetsWon ?? match.current_state?.mySetsWon ?? 0, 10);
      const oppSets = parseInt(match.opponent_sets_won ?? match.opponentSetsWon ?? match.current_state?.opponentSetsWon ?? 0, 10);
      if (mySets > oppSets) return true;
      if (oppSets > mySets) return false;
      let mScores = [];
      if (match.match_scores_json) {
        try { mScores = typeof match.match_scores_json === 'string' ? JSON.parse(match.match_scores_json) : match.match_scores_json; } catch(e) {}
      }
      if (!mScores.length && match.current_state?.matchScores) {
        mScores = match.current_state.matchScores;
      }
      if (Array.isArray(mScores) && mScores.length > 0) {
        let s1 = 0, s2 = 0;
        mScores.forEach(s => {
          const g1 = parseInt(s.myGames || s.player1_games || 0, 10);
          const g2 = parseInt(s.opponentGames || s.player2_games || 0, 10);
          if (g1 > g2) s1++;
          else if (g2 > g1) s2++;
        });
        if (s1 > s2) return true;
        if (s2 > s1) return false;
      }
      if (match.score && typeof match.score === 'string' && match.score !== '0-0') {
        const parts = match.score.split(/[\s,]+/).filter(Boolean);
        let p1 = 0, p2 = 0;
        parts.forEach(pt => {
          const [g1, g2] = pt.split('-').map(n => parseInt(n, 10) || 0);
          if (g1 > g2) p1++;
          else if (g2 > g1) p2++;
        });
        if (p1 > p2) return true;
        if (p2 > p1) return false;
      }
      return true;
    })();

    return isMe ? 'Me' : (match.opponent_name || match.opponent || 'Opponent');
  };

  const getFormattedScore = (match) => {
    if (!match) return '0-0';
    let scoreText = '';
    let matchScores = [];

    if (match.match_scores_json) {
      try {
        matchScores = typeof match.match_scores_json === 'string' ? JSON.parse(match.match_scores_json) : match.match_scores_json;
      } catch (e) {}
    }

    let cs = null;
    if (match.current_state) {
      try {
        cs = typeof match.current_state === 'string' ? JSON.parse(match.current_state) : match.current_state;
      } catch (e) {}
    }
    if (!matchScores.length && cs?.matchScores) {
      matchScores = cs.matchScores;
    }

    if (Array.isArray(matchScores) && matchScores.length > 0) {
      scoreText = matchScores.map(s => {
        const tb = s?.tbScore ? ` (${s.tbScore})` : '';
        return `${s?.myGames || s?.player1_games || 0}-${s?.opponentGames || s?.player2_games || 0}${tb}`;
      }).join(', ');
    } else if (match.score && match.score !== '0-0') {
      scoreText = match.score;
    }

    // Append active in-progress set score if match is in progress
    const isDone = String(match.status || '').toLowerCase() === 'completed' || match.current_state?.matchCompleted === true || (Boolean(match.winner) && String(match.winner).toLowerCase() !== 'inprogress' && String(match.winner).toLowerCase() !== 'in_progress');
    const isInProg = !isDone;
    if (isInProg) {
      if (cs && ((cs.myGamesWonInSet || 0) > 0 || (cs.opponentGamesWonInSet || 0) > 0)) {
        const activeScore = `${cs.myGamesWonInSet || 0}-${cs.opponentGamesWonInSet || 0}`;
        const parts = scoreText ? scoreText.split(',').map(s => s.trim()).filter(Boolean) : [];
        const setNum = parseInt(cs.setNumber, 10) || 1;

        if (setNum > parts.length) {
          parts.push(activeScore);
        } else if (setNum === parts.length) {
          parts[parts.length - 1] = activeScore;
        }
        scoreText = parts.join(', ');
      }
    }

    return scoreText || match.score || '0-0';
  };

  const safeMatches = Array.isArray(matches) ? matches : [];

  const filteredMatches = safeMatches.filter(match => {
    if (!match) return false;
    const opponentName = String(match.opponent_name || match.opponent || '').toLowerCase();
    const nameStr = String(match.name || match.match_name || '').toLowerCase();
    let matchDate = '';
    if (match.match_date || match.created_at) {
      try {
        matchDate = new Date(match.match_date || match.created_at).toLocaleDateString().toLowerCase();
      } catch (e) {}
    }
    const status = String(match.status || '').toLowerCase();
    const score = String(getFormattedScore(match) || '').toLowerCase();
    const winner = String(getMatchWinner(match) || '').toLowerCase();
    const st = searchTerm.toLowerCase();

    return opponentName.includes(st) ||
           nameStr.includes(st) ||
           matchDate.includes(st) ||
           status.includes(st) ||
           score.includes(st) ||
           winner.includes(st);
  });

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4">
        <input
          type="text"
          placeholder="Search matches..."
          className="p-2 border border-gray-300 rounded-md w-full sm:w-1/2"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="overflow-x-auto hidden sm:block"> {/* Hidden on small screens, block on larger */}
        <table className="min-w-full bg-white">
          <thead>
            <tr>
              <th className="py-2 px-4 border-b text-left">Opponent</th>
              <th className="py-2 px-4 border-b text-left">Date</th>
              <th className="py-2 px-4 border-b text-left">Score</th>
              <th className="py-2 px-4 border-b text-left">Status</th>
              <th className="py-2 px-4 border-b text-left">Winner</th>
              <th className="py-2 px-4 border-b text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMatches.map(match => (
              <tr key={match.match_id} className="hover:bg-gray-100">
                <td className="py-2 px-4 border-b cursor-pointer font-semibold" onClick={() => onSelectMatch(match)}>
                  {match.name && match.name.includes(' vs ') ? match.name : match.opponent_name}
                </td>
                <td className="py-2 px-4 border-b cursor-pointer text-gray-600" onClick={() => onSelectMatch(match)}>{new Date(match.match_date).toLocaleDateString()}</td>
                <td className="py-2 px-4 border-b cursor-pointer font-bold text-gray-800" onClick={() => onSelectMatch(match)}>{getFormattedScore(match)}</td>
                <td className="py-2 px-4 border-b cursor-pointer" onClick={() => onSelectMatch(match)}>
                  {(() => {
                    const isDone = String(match.status || '').toLowerCase() === 'completed' || match.current_state?.matchCompleted === true || (Boolean(match.winner) && String(match.winner).toLowerCase() !== 'inprogress' && String(match.winner).toLowerCase() !== 'in_progress');
                    return (
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${isDone ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                        {isDone ? 'Completed' : 'In Progress'}
                      </span>
                    );
                  })()}
                </td>
                <td className="py-2 px-4 border-b cursor-pointer" onClick={() => onSelectMatch(match)}>
                  {(() => {
                    const winnerName = getMatchWinner(match);
                    if (winnerName === 'N/A') return <span className="text-gray-400 font-medium">In Progress</span>;
                    const isMe = winnerName === 'Me';
                    return (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${isMe ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                        {isMe ? 'Won' : 'Lost'}
                      </span>
                    );
                  })()}
                </td>
                <td className="py-2 px-4 border-b">
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click from triggering
                      onDeleteMatch(match);
                    }}
                    className="text-red-600 hover:text-red-800"
                    title="Delete Match"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 000-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 11-2 0v6a1 1 0 112 0V8z" clipRule="evenodd" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="sm:hidden"> {/* Visible only on small screens */}
        {filteredMatches.length === 0 ? (
          <p className="text-center text-gray-600">No matches found.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredMatches.map(match => (
              <div key={match.match_id} className="bg-gray-50 p-4 rounded-lg shadow-md border border-gray-200">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">Match:</p>
                    <p className="font-semibold text-lg cursor-pointer" onClick={() => onSelectMatch(match)}>
                      {match.name && match.name.includes(' vs ') ? match.name : match.opponent_name}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteMatch(match);
                    }}
                    className="text-red-600 hover:text-red-800 ml-4"
                    title="Delete Match"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 000-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 11-2 0v6a1 1 0 112 0V8z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-500">Date:</p>
                    <p className="font-medium cursor-pointer" onClick={() => onSelectMatch(match)}>{new Date(match.match_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Score:</p>
                    <p className="font-medium cursor-pointer" onClick={() => onSelectMatch(match)}>{getFormattedScore(match)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status:</p>
                    {(() => {
                      const isDone = String(match.status || '').toLowerCase() === 'completed' || match.current_state?.matchCompleted === true || (Boolean(match.winner) && String(match.winner).toLowerCase() !== 'inprogress' && String(match.winner).toLowerCase() !== 'in_progress');
                      return (
                        <p className={`font-medium ${isDone ? 'text-green-600' : 'text-yellow-600'} cursor-pointer`} onClick={() => onSelectMatch(match)}>
                          {isDone ? 'Completed' : 'In Progress'}
                        </p>
                      );
                    })()}
                  </div>
                  <div>
                    <p className="text-gray-500">Result:</p>
                    {(() => {
                      const winnerName = getMatchWinner(match);
                      if (winnerName === 'N/A') return <p className="font-medium text-gray-500 cursor-pointer" onClick={() => onSelectMatch(match)}>In Progress</p>;
                      const isMe = winnerName === 'Me';
                      return (
                        <p className={`font-bold ${isMe ? 'text-green-600' : 'text-red-600'} cursor-pointer`} onClick={() => onSelectMatch(match)}>
                          {isMe ? 'Won' : 'Lost'}
                        </p>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchGrid;
